/**
 * Transport.ts — the abstraction both MockTransport and HttpWsTransport implement.
 *
 * REST methods return Promises. The WebSocket stream is exposed as a typed
 * subscription (`onMessage`) plus a `send` for client->server frames.
 */
import type {
  AutoplayRequest,
  AutoplayResponse,
  AutoplayStopRequest,
  BuyRequest,
  BuyResponse,
  ClientWsMessage,
  FairnessResponse,
  PictureInstanceResponse,
  PicturesResponse,
  RotateSeedResponse,
  ServerWsMessage,
  SessionRequest,
  SessionResponse,
} from './contract';

export interface Transport {
  /** "mock" = client-authoritative FUN-mode simulator; "http" = real Go server (money-authoritative). */
  readonly kind: 'mock' | 'http';

  /** Create or resume a session. */
  createSession(req: SessionRequest): Promise<SessionResponse>;

  /** Gallery list. */
  getPictures(): Promise<PicturesResponse>;

  /** Instantiate a picture for this session (returns concrete cells). */
  selectPicture(pictureId: string): Promise<PictureInstanceResponse>;

  /** Buy a color (debits balance, pre-rolls the outcome server-side). */
  buyColor(req: BuyRequest): Promise<BuyResponse>;

  /** Start autoplay. */
  startAutoplay(req: AutoplayRequest): Promise<AutoplayResponse>;

  /** Stop autoplay. */
  stopAutoplay(req: AutoplayStopRequest): Promise<AutoplayResponse>;

  /** Rotate (reveal) the server seed and issue a new commitment. */
  rotateSeed(): Promise<RotateSeedResponse>;

  /** Fairness record for a settled bet. */
  getFairness(betId: string): Promise<FairnessResponse>;

  /** Open the realtime channel. Resolves once connected. */
  connect(): Promise<void>;

  /** Send a client frame. */
  send(msg: ClientWsMessage): void;

  /** Subscribe to server frames. Returns an unsubscribe fn. */
  onMessage(fn: (msg: ServerWsMessage) => void): () => void;

  /** Tear down. */
  dispose(): void;
}

/** Which transport to use, resolved from Vite env with a safe default. */
export type BackendMode = 'mock' | 'http';

export function resolveBackendMode(): BackendMode {
  const v = (import.meta.env?.VITE_BACKEND ?? 'mock').toLowerCase();
  return v === 'http' ? 'http' : 'mock';
}

export function apiBase(): string {
  return import.meta.env?.VITE_API_BASE ?? 'http://localhost:8080';
}
